import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "components/ui/toast";

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || fallback;

export const useGetJurisdictionRulesQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.JURISDICTION_RULES],
    queryFn: () => api.get(API_ROUTES.JURISDICTION_RULES),
  });
  return { data: data?.data?.data ?? [], isPending, isError, error };
};

// Fixed in the backend's code, so it never goes stale within a session.
export const useGetJurisdictionRuleOptionsQuery = () => {
  const { data, isPending } = useQuery({
    queryKey: [API_ROUTES.JURISDICTION_RULE_OPTIONS],
    queryFn: () => api.get(API_ROUTES.JURISDICTION_RULE_OPTIONS),
    staleTime: Infinity,
  });
  return {
    data: data?.data?.data ?? {
      checkTypes: [],
      steps: [],
      judgedOn: [],
      waitingPeriods: [],
    },
    isPending,
  };
};

// One write to a rule: drafting, editing, or a step in its life (simulate, approve, retire).
const useRuleMutation = (mutationFn, success, failure) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      showToast("success", success);
      queryClient.invalidateQueries({
        queryKey: [API_ROUTES.JURISDICTION_RULES],
      });
      // The register counts the rules quoting each law.
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.LEGISLATION] });
    },
    onError: (err) => showToast("error", errorMessage(err, failure)),
  });
};

export const useDraftJurisdictionRuleMutation = () =>
  useRuleMutation(
    (rule) => api.post(API_ROUTES.JURISDICTION_RULES, rule),
    "Draft saved",
    "Failed to save the draft",
  );

export const useEditJurisdictionRuleMutation = () =>
  useRuleMutation(
    ({ id, ...rule }) =>
      api.put(`${API_ROUTES.JURISDICTION_RULES}/${id}`, rule),
    "Draft saved",
    "Failed to save the draft",
  );

export const useSimulateJurisdictionRuleMutation = () =>
  useRuleMutation(
    (id) => api.put(`${API_ROUTES.JURISDICTION_RULES}/${id}/simulate`),
    "Simulated",
    "Failed to simulate the rule",
  );

export const useApproveJurisdictionRuleMutation = () =>
  useRuleMutation(
    (id) => api.put(`${API_ROUTES.JURISDICTION_RULES}/${id}/approve`),
    "Approved. Every server applies it within a minute.",
    "Failed to approve the rule",
  );

export const useRetireJurisdictionRuleMutation = () =>
  useRuleMutation(
    (id) => api.put(`${API_ROUTES.JURISDICTION_RULES}/${id}/retire`),
    "Retired",
    "Failed to retire the rule",
  );
